import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorFn } from '@app/core/state/angularTypes';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNGConfig } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { LanguageType } from 'src/app/core/entity/locale';
import { TodayDate } from 'src/app/shared/misc/today.date';
import { MAX_CALENDAR_DATE, MIN_CALENDAR_DATE } from '../../misc/dates';

@Component({
  selector: 'app-calendar',
  template: `
    @if (label) {
      <label class="bl-core-input-label" for="{{ inputId }}">{{ label }}</label>
    }
    <p-calendar
      [ngModel]="primeNGDate"
      (ngModelChange)="primeNGDate = $event"
      [showIcon]="true"
      [dataType]="dataType"
      [minDate]="minSelectableDate"
      [maxDate]="maxSelectableDate"
      [style]="style"
      [disabled]="disabled"
      [firstDayOfWeek]="1"
      [showOnFocus]="false"
      styleClass="{{ styleClass }}"
      appendTo="{{ appendTo }}"
      inputId="{{ inputId }}"
      title="{{ title }}"
      (onFocus)="onDateTextfieldFocus()"
      (onBlur)="onDateTextfieldBlur()"
      (onClose)="onDateSelectorClose()"
      (onInput)="onInput($event)"
      [class]="invalid ? 'ng-invalid ng-dirty' : ''"></p-calendar>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CalendarComponent,
    },
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule],
})
export class CalendarComponent implements ControlValueAccessor {
  private _primeNGDate: any; //Date | string;
  private _dataType: 'string' | 'date' = 'string';
  private _touched = false;
  private _hasTextfieldFocus = false;
  private _isDateSelectorOpen = false;
  private _validDateStringRegex = new RegExp(/^[0-9]{8}$/);
  private _disabled = false;
  private _todayDate: TodayDate;

  @Input() minSelectableDate = MIN_CALENDAR_DATE;
  @Input() maxSelectableDate = MAX_CALENDAR_DATE;
  @Input() defaultDate: Date | string = 'today';
  @Input() styleClass: string;
  @Input() style: any;
  @Input() appendTo: string;
  @Input() inputId: string;
  @Input() title: string;
  @Input() invalid = false;
  @Input() label: string;

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input()
  get dataType() {
    return this._dataType;
  }
  set dataType(value: 'date' | 'string') {
    this._dataType = value;
  }

  @Input()
  set selectedDate(value: any) {
    this.writeValue(value);
  }

  @Output() selectedDateChange: EventEmitter<string> = new EventEmitter();

  get primeNGDate() {
    return this._primeNGDate;
  }
  set primeNGDate(value: any) {
    this._primeNGDate = value;
    this.onChange(this._todayDate.getIsoFormattedDateByLocale(value));
    this.selectedDateChange.emit(this._todayDate.getIsoFormattedDateByLocale(value));
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private config: PrimeNGConfig,
    private translocoService: TranslocoService,
  ) {
    this._todayDate = new TodayDate(translocoService.getActiveLang() as LanguageType);
    this.translocoService.langChanges$.pipe(takeUntilDestroyed()).subscribe((lang: LanguageType) => {
      // Gets the current date in ISO format before changing the locale
      const currentDateInIsoFormat = this._todayDate.getIsoFormattedDateByLocale(this.primeNGDate);

      this._todayDate = new TodayDate(lang);

      // rewrites the date in the format of the new locale
      this.writeValue(currentDateInIsoFormat);
      this.changeDetectorRef.markForCheck();
    });
  }

  onChange: ControlValueAccessorFn = () => {};

  onTouched: ControlValueAccessorFn = () => {};

  writeValue(value: Date | string) {
    try {
      this.primeNGDate = value && this._todayDate.formatDateByLocale(new Date(value));
    } catch {
      this.primeNGDate = null;
    }
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(onChange: ControlValueAccessorFn) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: ControlValueAccessorFn) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this._touched) {
      this.onTouched();
      this._touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    this.changeDetectorRef.markForCheck();
  }

  onInput(e: { target?: { value: string } }) {
    const inputValue = e?.target?.value;

    if (this.isValidDateString(inputValue)) {
      this.primeNGDate = this.parseNumberStringToDate(inputValue);
    }
  }

  onDateTextfieldFocus() {
    this._hasTextfieldFocus = true;
    this._isDateSelectorOpen = true;
  }

  onDateTextfieldBlur() {
    this._hasTextfieldFocus = false;
    this.updateDateToDefaultValue();
  }

  onDateSelectorClose() {
    this._hasTextfieldFocus = false;
    this._isDateSelectorOpen = false;
    this.updateDateToDefaultValue();
  }

  private isValidDateString(value: string) {
    return this._validDateStringRegex.test(value);
  }

  private parseNumberStringToDate(value: string): null | string {
    const separatorRegex = /[-./]/gi;
    const dateFormat = this.config.translation.dateFormat || 'yy-mm-dd';
    const formatAsArray = dateFormat.split(separatorRegex);
    const separator = dateFormat.match(separatorRegex)?.[0] || '-';

    const formatToValueMap = { yy: 4, mm: 2, dd: 2 };
    const dateValueArray = { yy: '', mm: '', dd: '' };

    formatAsArray.reduce((start, format) => {
      const length = formatToValueMap[format];
      const valuePart = value.slice(start, start + length);
      dateValueArray[format] = valuePart;
      return start + length;
    }, 0);

    return formatAsArray.map((format) => dateValueArray[format]).join(separator); // return the date in the format of the calendar
  }

  private updateDateToDefaultValue() {
    if (this._hasTextfieldFocus || this._isDateSelectorOpen) {
      return; // if field still has focus or date selector is open, we have to skip this updating
    }

    if (!this.primeNGDate && this.defaultDate) {
      this.primeNGDate = this.getDefaultDate();
    }
  }

  public getDefaultDate() {
    if (this.defaultDate !== 'today') {
      return this.defaultDate;
    }

    if (this.dataType === 'string') {
      return new TodayDate().getTodayAsString();
    }
    return new Date();
  }
}
